import { PRIMUM_SUPPORT_USER } from '@/constants/users';
import type { InboxEntry } from '@/services/conversations/inbox';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameOrFallback } from '@/util/getProfileNameAndInitials';

export const PRIMUM_SUPPORT_LABEL = 'Primum Support';

const UNNAMED_MEMBER_LABEL = 'Member';

export type SupportThreadSubject = NonNullable<InboxEntry['subject']>;

/**
 * Who the reader is in a support thread. A support thread is always about one
 * member, its subject; anyone else reading it is Primum Support, which in the
 * app means staff acting as the support account.
 *
 * Decided by the viewer's own id against the subject rather than by a staff
 * flag, so it holds for whichever account the viewer is acting as. A thread
 * with no subject is read as the member's own, since only the member's view
 * can be drawn without one.
 */
export function isMemberView(
  subject: SupportThreadSubject | null | undefined,
  viewerId: string | undefined
): boolean {
  return !subject || String(subject.id) === viewerId;
}

/**
 * The other party, as the thread's title and as the name on their messages.
 * The member sees Primum Support; Primum Support sees the member. The staff
 * member behind a support message is never named.
 */
export function supportThreadCounterpartName(
  subject: SupportThreadSubject | null | undefined,
  viewerId: string | undefined
): string {
  if (isMemberView(subject, viewerId)) {
    return PRIMUM_SUPPORT_LABEL;
  }

  return getProfileNameOrFallback(subject, UNNAMED_MEMBER_LABEL);
}

/**
 * The other party's picture. A member sees the Primum Support avatar, the same
 * one support messages and support inquiries have always carried, so a thread
 * reads as the same Primum it has always been. Primum Support sees the
 * member's own avatar, or none when the member has not set one.
 */
export function supportThreadCounterpartAvatarUrl(
  subject: SupportThreadSubject | null | undefined,
  viewerId: string | undefined
): string | undefined {
  if (isMemberView(subject, viewerId)) {
    return PRIMUM_SUPPORT_USER.avatarSrc;
  }

  return subject?.avatar ? getAvatarUrl(subject.avatar) || undefined : undefined;
}
